import React, { useCallback } from 'react';
import { OrderingValues, SongTiktokStats, TrndsClientSongTiktokStats } from '@round/api';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { CellProps, Column } from 'react-table';
import styles from './ExploreSongsTable.module.css';
import Skeleton from '../../../../ui/Skeleton/Skeleton';
import Image from '../../../../ui/Image/Image';
import { formatNumberToKNotation, numberWithCommas, roundTo2Dp } from '../../../../helpers';
import cn from 'classnames';
import BarsSmallChart from '../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import TruncatedTextCell from '../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import { ReactComponent as MusicIcon } from '../../../../assets/MusicIcon.svg';
import AddClientSongToCollection from '../../features/AddClientSongToCollection/AddClientSongToCollection';

export type ExploreSongsSortableKeys = keyof Pick<
    TrndsClientSongTiktokStats,
    | 'total_video_count'
    | 'total_video_count_daily_change'
    | 'total_video_count_daily_change_relative'
    | 'total_video_count_weekly_change'
    | 'total_video_count_weekly_change_relative'
>;

type ExploreSongsTableProps = {
    ordering?: OrderingValues<ExploreSongsSortableKeys>;
    onRowClick?: (row: TrndsClientSongTiktokStats) => void;
    clientId: number;
} & Pick<
    PaginationTableProps<TrndsClientSongTiktokStats>,
    'loading' | 'data' | 'count' | 'page' | 'setPage' | 'pageSize' | 'setPageSize' | 'noDataLabel'
>;

const ExploreSongsTable = ({
    loading,
    data,
    page,
    setPage,
    setPageSize,
    pageSize,
    count,
    ordering,
    onRowClick,
    clientId,
    noDataLabel,
}: ExploreSongsTableProps) => {
    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (ordering?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [ordering]
    );

    const columns: Column<TrndsClientSongTiktokStats>[] = [
        {
            Header: 'Song',
            accessor: 'song',
            Cell: ({ value }) => {
                if (loading) {
                    return <Skeleton />;
                }

                return (
                    <div className={styles.songContainer}>
                        <Image className={styles.songImage} src={value.image ?? ''} alt={value.title} />
                        <div className={styles.songDetails}>
                            <a
                                href={value.spotify_song_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.songTitle}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {value.title}
                            </a>
                            <p className={styles.songAuthor}>
                                <MusicIcon className={styles.musicIcon} />
                                {value.artists.map((a) => a.name).join(', ')}
                            </p>
                        </div>
                    </div>
                );
            },
        },
        {
            Header: 'Audios',
            accessor: 'audio_count',
            Cell: ({ value }) => {
                if (loading) {
                    return <Skeleton />;
                }

                return <>{numberWithCommas(value)}</>;
            },
        },
        {
            Header: 'Creations',
            accessor: 'total_video_count',
            Cell: ({ value, row: { original } }) => {
                if (loading) {
                    return <Skeleton />;
                }

                const badgeValue =
                    ordering && ordering !== '-total_video_count'
                        ? Number(original[ordering.slice(1) as ExploreSongsSortableKeys])
                        : null;

                return (
                    <>
                        <span>{formatNumberToKNotation(value)}</span>
                        {typeof badgeValue === 'number' && (
                            <span
                                className={cn(styles.growthBadge, {
                                    [styles.increasing]: badgeValue >= 0,
                                    [styles.decreasing]: badgeValue < 0,
                                })}
                            >
                                {badgeValue > 0 && '+'}
                                {formatVideoCountBadgeValue(badgeValue)}
                            </span>
                        )}
                    </>
                );
            },
        },
        {
            Header: 'Growth',
            accessor: 'standardised_data',
            Cell: ({ value }) => {
                if (loading) {
                    return <Skeleton />;
                }

                return (
                    <div className={styles.growth}>
                        <BarsSmallChart
                            color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                            values={value.daily_total_video_counts
                                .slice(-7)
                                .map((item) => ({ ...item, value: item.change }))}
                        />
                    </div>
                );
            },
        },
        {
            Header: 'Add to collection',
            accessor: 'song_collection_ids',
            Cell: ({ value, row }: CellProps<SongTiktokStats>) => {
                if (loading) {
                    return <Skeleton />;
                }

                return (
                    <AddClientSongToCollection
                        clientId={clientId}
                        songId={row.original.song.id}
                        collectionsId={value}
                        renderAnchor={({ setRef, selectedCollectionsCount }) => (
                            <div ref={setRef} onClick={(e) => e.stopPropagation()}>
                                <TruncatedTextCell>
                                    {`Added to ${selectedCollectionsCount} collection${
                                        selectedCollectionsCount !== 1 ? 's' : ''
                                    }`}
                                </TruncatedTextCell>
                            </div>
                        )}
                    />
                );
            },
        },
    ];

    return (
        <PaginationTable
            tableClassName={styles.table}
            disableSortBy
            loading={loading}
            data={data}
            columns={columns}
            count={count}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            noDataLabel={noDataLabel}
            onRowClick={onRowClick}
        />
    );
};

export default ExploreSongsTable;
